import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { HandHeart, Users, BadgeCheck, UserPlus, FileText, Download, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { displayEventDate } from "@/constants";

const EMPTY = { adults: "", children_count: 1, children_names: "", phone: "", email: "", address: "", image_consent: false, first_name: "", last_name: "" };
const EMPTY_VOL = { first_name: "", last_name: "", phone: "", email: "", message: "" };
const LEGAL_LABELS = { reglement: "Règlement général", cgv: "Conditions générales de vente (CGV)" };

export default function ParticipationDialog({ event, open, onOpenChange }) {
  const [role, setRole] = useState("beneficiaire");
  const [quality, setQuality] = useState("adherent");
  const [form, setForm] = useState(EMPTY);
  const [vol, setVol] = useState(EMPTY_VOL);
  const [sending, setSending] = useState(false);
  const [legalDocs, setLegalDocs] = useState(null);
  const [downloaded, setDownloaded] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [attendance, setAttendance] = useState("");
  const [givesProxy, setGivesProxy] = useState(false);
  const [proxyFirst, setProxyFirst] = useState("");
  const [proxyLast, setProxyLast] = useState("");
  const [internalCode, setInternalCode] = useState("");
  const [color, setColor] = useState("");

  useEffect(() => {
    if (!open) return;
    setDownloaded({});
    setTermsAccepted(false);
    api.get("/legal-docs").then((r) => setLegalDocs(r.data || {})).catch(() => setLegalDocs({}));
  }, [open]);

  const legalKinds = legalDocs ? Object.keys(legalDocs) : [];
  const legalGateActive = legalKinds.length > 0;
  const allDownloaded = legalGateActive && legalKinds.every((k) => downloaded[k]);
  const mo = !!(event?.members_only && role === "beneficiaire");
  const namesOk = !mo || (form.first_name.trim() !== "" && form.last_name.trim() !== "");
  const attendanceOk = !mo || attendance !== "";
  const codeOk = !mo || !event?.code_required || /^[A-Za-z0-9]{6}$/.test(internalCode.trim());
  const colorOk = !mo || !event?.color_required || color !== "";
  const proxyOk = !mo || !givesProxy || (proxyFirst.trim() !== "" && proxyLast.trim() !== "");
  const canSubmit = namesOk && attendanceOk && codeOk && colorOk && proxyOk && (!legalGateActive || (allDownloaded && termsAccepted));

  const downloadDoc = (kind) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/legal-docs/${kind}/download`, "_blank");
    setDownloaded((d) => ({ ...d, [kind]: true }));
  };

  const field = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const vfield = (key, value) => setVol((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!event) return;
    setSending(true);
    try {
      await api.post("/event-participations", {
        event_id: event.id,
        event_title: event.title,
        adults: form.adults,
        children_count: Number(form.children_count) || 0,
        children_names: form.children_names,
        phone: form.phone,
        email: form.email,
        address: form.address,
        member_status: quality,
        first_name: form.first_name,
        last_name: form.last_name,
        attendance,
        gives_proxy: givesProxy,
        proxy_name: givesProxy ? `${proxyFirst.trim()} ${proxyLast.trim()}`.trim() : "",
        internal_code: internalCode.trim(),
        color,
        image_consent: form.image_consent,
        terms_accepted: termsAccepted,
      });
      toast.success("Inscription envoyée ! Un email de confirmation vous a été adressé.");
      setForm(EMPTY);
      setDownloaded({});
      setTermsAccepted(false);
      setAttendance("");
      setGivesProxy(false);
      setProxyFirst("");
      setProxyLast("");
      setInternalCode("");
      setColor("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const submitVolunteer = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/volunteers/signup", {
        first_name: vol.first_name,
        last_name: vol.last_name,
        email: vol.email,
        phone: vol.phone,
        message: `[${event?.title || "Événement"}] ${vol.message}`.trim(),
        member_status: quality,
      });
      toast.success("Inscription bénévole envoyée ! Le bureau vous recontactera.");
      setVol(EMPTY_VOL);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (event?.registration_open === false) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="participate-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800">{event.title}</DialogTitle>
          </DialogHeader>
          <div className="rounded-2xl border-4 border-slate-400/80 bg-gradient-to-br from-slate-100 to-blue-100/70 px-6 py-10 text-center shadow-inner" data-testid="participate-closed-cartouche">
            <Lock className="h-10 w-10 mx-auto text-slate-500 mb-3" />
            <p className="text-2xl font-black uppercase tracking-widest text-slate-600">Événement complet</p>
            <p className="text-sm font-bold uppercase tracking-widest text-blue-500 mt-1.5">Inscription clôturée</p>
            <p className="text-sm text-slate-600 mt-5 leading-relaxed">
              Cet événement est désormais complet. L'inscription est clôturée. Merci de votre intérêt.
            </p>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mt-5">CETF Association.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="participate-dialog">
        <DialogHeader>
          <DialogTitle>Je participe à l'événement</DialogTitle>
        </DialogHeader>
        {event && (
          <p className="text-sm text-slate-600 -mt-1">
            <span className="font-semibold text-slate-800">{event.title}</span>
            {event.date ? ` — ${displayEventDate(event.date)}` : ""}
            {event.location ? ` · ${event.location}` : ""}
          </p>
        )}
        <div className="grid grid-cols-2 gap-2.5" role="group" aria-label="Votre rôle lors de l'événement">
          <button type="button" onClick={() => setRole("beneficiaire")} data-testid="participate-role-beneficiary-btn"
            className={`rounded-xl border-2 p-3 text-left transition-all ${role === "beneficiaire" ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
            <Users className={`h-4 w-4 mb-1 ${role === "beneficiaire" ? "text-emerald-600" : "text-slate-400"}`} />
            <p className="text-sm font-semibold text-slate-800">Je suis bénéficiaire</p>
            <p className="text-[11px] text-slate-500 leading-snug">Je viens profiter de l'événement en famille</p>
          </button>
          <button type="button" onClick={() => setRole("benevole")} data-testid="participate-role-volunteer-btn"
            className={`rounded-xl border-2 p-3 text-left transition-all ${role === "benevole" ? "border-amber-500 bg-amber-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
            <HandHeart className={`h-4 w-4 mb-1 ${role === "benevole" ? "text-amber-600" : "text-slate-400"}`} />
            <p className="text-sm font-semibold text-slate-800">Je suis bénévole</p>
            <p className="text-[11px] text-slate-500 leading-snug">Je donne un coup de main à l'organisation</p>
          </button>
        </div>

        {event?.members_only ? (
          <div className="space-y-2.5">
            <div className="rounded-xl border-2 border-violet-200 bg-violet-50 px-3.5 py-2.5" data-testid="participate-members-only-note">
              <p className="text-xs font-semibold text-violet-700">Événement réservé aux adhérents et au bureau de l'association</p>
            </div>
            <div className="grid grid-cols-2 gap-2.5" role="group" aria-label="Votre qualité">
              <button type="button" onClick={() => setQuality("adherent")} data-testid="participate-quality-member-btn"
                className={`rounded-xl border-2 p-3 text-left transition-all ${quality === "adherent" ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
                <BadgeCheck className={`h-4 w-4 mb-1 ${quality === "adherent" ? "text-emerald-600" : "text-slate-400"}`} />
                <p className="text-sm font-semibold text-slate-800">Adhérent</p>
                <p className="text-[11px] text-slate-500 leading-snug">Membre de l'association à jour de cotisation</p>
              </button>
              <button type="button" onClick={() => setQuality("bureau")} data-testid="participate-quality-bureau-btn"
                className={`rounded-xl border-2 p-3 text-left transition-all ${quality === "bureau" ? "border-violet-500 bg-violet-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
                <Users className={`h-4 w-4 mb-1 ${quality === "bureau" ? "text-violet-600" : "text-slate-400"}`} />
                <p className="text-sm font-semibold text-slate-800">Membre du bureau</p>
                <p className="text-[11px] text-slate-500 leading-snug">Présidence, trésorerie, secrétariat…</p>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="p-firstname">Prénom *</Label>
                <Input id="p-firstname" maxLength={80} data-testid="participate-firstname-input"
                  value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="p-lastname">Nom *</Label>
                <Input id="p-lastname" maxLength={80} data-testid="participate-lastname-input"
                  value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Serez-vous présent(e) ? *</Label>
              <div className="grid grid-cols-2 gap-2.5">
                <button type="button" onClick={() => setAttendance("present")} data-testid="participate-attendance-present"
                  className={`rounded-xl border-2 p-2.5 text-sm font-semibold transition-all ${attendance === "present" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  Présent(e)
                </button>
                <button type="button" onClick={() => setAttendance("absent")} data-testid="participate-attendance-absent"
                  className={`rounded-xl border-2 p-2.5 text-sm font-semibold transition-all ${attendance === "absent" ? "border-slate-500 bg-slate-100 text-slate-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  Pas présent(e)
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2.5 rounded-lg border border-slate-200 p-3">
              <Checkbox id="p-proxy" checked={givesProxy} onCheckedChange={(v) => setGivesProxy(v === true)} data-testid="participate-proxy-checkbox" />
              <Label htmlFor="p-proxy" className="text-sm font-normal text-slate-700 cursor-pointer">Donne mon pouvoir à :</Label>
            </div>
            {givesProxy && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="participate-proxy-fields">
                <div className="space-y-1.5">
                  <Label htmlFor="p-proxy-first">Prénom du mandataire *</Label>
                  <Input id="p-proxy-first" maxLength={80} data-testid="participate-proxy-firstname"
                    value={proxyFirst} onChange={(e) => setProxyFirst(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="p-proxy-last">Nom du mandataire *</Label>
                  <Input id="p-proxy-last" maxLength={80} data-testid="participate-proxy-lastname"
                    value={proxyLast} onChange={(e) => setProxyLast(e.target.value)} />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="p-code">Code d'authentification interne *</Label>
              <Input id="p-code" maxLength={6} placeholder="6 caractères" autoComplete="off"
                className="font-mono tracking-[0.3em] uppercase max-w-[180px]" data-testid="participate-internal-code"
                value={internalCode} onChange={(e) => setInternalCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 6))} />
              <p className="text-xs text-slate-400">Code alphanumérique à 6 caractères transmis en interne par l'association.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Couleur de vérification *</Label>
              <div className="flex gap-2.5" role="group" aria-label="Couleur de vérification">
                {[{ id: "rouge", c: "#dc2626" }, { id: "bleu", c: "#2563eb" }, { id: "jaune", c: "#eab308" }, { id: "noir", c: "#171717" }].map((col) => (
                  <button key={col.id} type="button" onClick={() => setColor(col.id)}
                    data-testid={`participate-color-${col.id}`} aria-label={`Couleur ${col.id}`}
                    className={`h-9 w-9 rounded-full border-4 transition-all ${color === col.id ? "border-emerald-500 scale-110 shadow-md" : "border-white shadow"}`}
                    style={{ backgroundColor: col.c }} />
                ))}
              </div>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-2.5" role="group" aria-label="Votre qualité">
          <button type="button" onClick={() => setQuality("adherent")} data-testid="participate-quality-member-btn"
            className={`rounded-xl border-2 p-3 text-left transition-all ${quality === "adherent" ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
            <BadgeCheck className={`h-4 w-4 mb-1 ${quality === "adherent" ? "text-emerald-600" : "text-slate-400"}`} />
            <p className="text-sm font-semibold text-slate-800">Je suis déjà adhérent</p>
            <p className="text-[11px] text-slate-500 leading-snug">Membre de l'association à jour de cotisation</p>
          </button>
          <button type="button" onClick={() => setQuality("non_adherent")} data-testid="participate-quality-not-member-btn"
            className={`rounded-xl border-2 p-3 text-left transition-all ${quality === "non_adherent" ? "border-sky-500 bg-sky-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
            <UserPlus className={`h-4 w-4 mb-1 ${quality === "non_adherent" ? "text-sky-600" : "text-slate-400"}`} />
            <p className="text-sm font-semibold text-slate-800">Pas encore adhérent</p>
            <p className="text-[11px] text-slate-500 leading-snug">Je découvre l'association</p>
          </button>
        </div>
        )}

        {role === "benevole" ? (
          <form onSubmit={submitVolunteer} className="space-y-4" data-testid="volunteer-signup-section">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-600">S'inscrire en tant que bénévole</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="v-firstname">Prénom *</Label>
                <Input id="v-firstname" required maxLength={80} data-testid="volunteer-firstname-input"
                  value={vol.first_name} onChange={(e) => vfield("first_name", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="v-lastname">Nom *</Label>
                <Input id="v-lastname" required maxLength={80} data-testid="volunteer-lastname-input"
                  value={vol.last_name} onChange={(e) => vfield("last_name", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="v-phone">Téléphone *</Label>
                <Input id="v-phone" type="tel" required maxLength={30} placeholder="06 …" data-testid="volunteer-phone-input"
                  value={vol.phone} onChange={(e) => vfield("phone", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="v-email">Adresse mail *</Label>
                <Input id="v-email" type="email" required placeholder="vous@exemple.fr" data-testid="volunteer-email-input"
                  value={vol.email} onChange={(e) => vfield("email", e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-message">Message / disponibilités <span className="text-slate-400 font-normal">(facultatif)</span></Label>
              <Input id="v-message" maxLength={500} placeholder="Ex. : disponible dès 13 h pour l'installation" data-testid="volunteer-message-input"
                value={vol.message} onChange={(e) => vfield("message", e.target.value)} />
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Votre inscription est transmise au bureau de l'association, qui vous recontactera pour organiser votre venue.
            </p>
            <Button type="submit" disabled={sending} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="volunteer-submit-btn">
              {sending ? "Envoi en cours…" : "Envoyer mon inscription bénévole"}
            </Button>
          </form>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="p-adults">Nom et prénom du ou des adultes *</Label>
            <Input id="p-adults" data-testid="participate-adults-input" required maxLength={300}
              placeholder="Ex. : Marie et Jean Dupont"
              value={form.adults} onChange={(e) => field("adults", e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-count">Nombre d'enfants *</Label>
              <Input id="p-count" type="number" min={0} max={30} required data-testid="participate-children-count-input"
                value={form.children_count} onChange={(e) => field("children_count", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-phone">Téléphone *</Label>
              <Input id="p-phone" type="tel" required maxLength={30} placeholder="06 …" data-testid="participate-phone-input"
                value={form.phone} onChange={(e) => field("phone", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-children">Nom et prénom des enfants *</Label>
            <Input id="p-children" required maxLength={600} placeholder="Ex. : Lina Dupont, Noah Dupont" data-testid="participate-children-names-input"
              value={form.children_names} onChange={(e) => field("children_names", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-email">Adresse mail *</Label>
            <Input id="p-email" type="email" required placeholder="vous@exemple.fr" data-testid="participate-email-input"
              value={form.email} onChange={(e) => field("email", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-address">Adresse postale <span className="text-slate-400 font-normal">(facultatif)</span></Label>
            <Input id="p-address" maxLength={300} placeholder="Rue, code postal, commune" data-testid="participate-address-input"
              value={form.address} onChange={(e) => field("address", e.target.value)} />
          </div>
          <div className="flex items-start gap-2.5 bg-slate-50 rounded-xl p-3.5">
            <Checkbox id="p-consent" data-testid="participate-image-consent-checkbox" className="mt-0.5"
              checked={form.image_consent} onCheckedChange={(v) => field("image_consent", v === true)} />
            <Label htmlFor="p-consent" className="text-sm font-normal leading-snug text-slate-600 cursor-pointer">
              J'autorise la prise et la diffusion de photos des participants sur les réseaux sociaux
              et supports de communication de l'association (droit à l'image).
            </Label>
          </div>
          {legalGateActive && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 space-y-3" data-testid="legal-docs-block">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Documents à consulter avant validation</p>
              <div className="flex flex-col gap-2">
                {legalKinds.map((kind) => (
                  <button key={kind} type="button" onClick={() => downloadDoc(kind)} data-testid={`legal-download-${kind}`}
                    className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                      downloaded[kind]
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700"}`}>
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">Télécharger : {LEGAL_LABELS[kind] || kind}</span>
                    {downloaded[kind]
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      : <Download className="h-4 w-4 shrink-0" />}
                  </button>
                ))}
              </div>
              <div className={`flex items-start gap-2.5 rounded-lg p-3 transition-colors ${allDownloaded ? "bg-white border border-slate-200" : "bg-slate-100"}`}>
                <Checkbox id="p-terms" data-testid="participate-terms-checkbox" className="mt-0.5"
                  disabled={!allDownloaded}
                  checked={termsAccepted} onCheckedChange={(v) => setTermsAccepted(v === true)} />
                <Label htmlFor="p-terms" className={`text-sm font-normal leading-snug ${allDownloaded ? "text-slate-700 cursor-pointer" : "text-slate-400"}`}>
                  J'ai lu et j'accepte le règlement général et les conditions générales de vente de l'association. *
                  {!allDownloaded && (
                    <span className="block text-xs text-slate-400 mt-0.5">Téléchargez les documents ci-dessus pour pouvoir cocher cette case.</span>
                  )}
                </Label>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400 leading-relaxed">
            Ces informations sont uniquement utilisées pour l'organisation de l'événement et ne sont jamais partagées avec des tiers.
          </p>
          <Button type="submit" disabled={sending || !canSubmit} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="participate-submit-btn">
            {sending ? "Envoi en cours…" : "Envoyer ma participation"}
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
